import type { FC } from "react";
import { useRef, useState, useEffect } from "react";
import { SWReactTableWrapperContextConsumer } from "app/components/React/Table/SWReactTableWrapperSelectionContext";
import { TableSelection } from "pro-features/components/TableSelection/src/TableSelection";
import { ETableSelectionNewGroupDropdownMode } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import { TableSelectionGroupWebsiteComponent } from "pro-features/components/TableSelection/src/TableSelectionGroupWebsiteComponent";
import { translate } from "app/filters/i18nFilter";
import { SegmentModal } from "./segments-modal/SegmentModal";
import type { UserAssetName } from "app/UtilitiesAndConstants/types/userAssetName";
import { getDefaultValueHof } from "app/UtilitiesAndConstants/types/userAssetName";
import type { Step } from "app/components/React/TableSelectionComponents/segment-selection/segments-modal/stepsComponents";
import { validateSegmentName } from "app/pages/segments/redesigned-wizard/segment-wizard/utils/validateSegmentName";
import { useOnEscape } from "app/custom-hooks/useOnKeyUp";
import SegmentsApiService from "app/services/segments/segmentsWizardService";
import type { ICustomSegment, ISegmentRuleItems } from "app/services/segments/segmentsWizardServiceTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import { customSegmentCreatedUpdated } from "app/actions/segmentsModuleActions";
import { useDispatch } from "react-redux";
import { showErrorToast } from "app/actions/toast_actions";
import Sentry from "@similarweb/sentry-logger";
import { allTrackers } from "app/services/track/track";

interface SegmentSelectionProps {
    selectedRows: Array<unknown>;
    textKey: string;
    domain: string;
    clearSelection: VoidFunction;
    getRules: (selectedRows: Array<unknown>) => ISegmentRuleItems;
    createGroupButtonKey?: string;
    addToGroupButtonKey?: string;
    initialIsVisible?: boolean;
    appendTo?: string;
    isLoading?: boolean;
    defaultAssetName?: string;
}

export const SegmentSelection: FC<SegmentSelectionProps> = (props) => {
    const {
        selectedRows,
        addToGroupButtonKey,
        textKey,
        domain,
        clearSelection,
        getRules,
        initialIsVisible = true,
        createGroupButtonKey = "Create New Segment",
        appendTo = ".swReactTable-header-wrapper",
        isLoading = false,
        defaultAssetName = "",
    } = props;

    const dropdownRef = useRef(null);
    const [segmentName, setSegmentName] = useState<UserAssetName>(getDefaultValueHof(defaultAssetName));
    useEffect(() => {
        const getDefaultValue = getDefaultValueHof(defaultAssetName);
        setSegmentName(getDefaultValue());
    }, [defaultAssetName]);
    const [isCreateNewListModalOpen, setIsCreateNewListModalOpen] = useState(false);
    const [popupStep, setPopupStep] = useState<Step>("Wizard");
    const [isCreatingSegment, setIsCreatingSegment] = useState<boolean>(false);
    const segmentCreated = useRef<ICustomSegment>(null);
    const dispatch = useDispatch();

    const closeModalAndReset = () => {
        segmentCreated.current = null;
        setIsCreateNewListModalOpen(false);
        setSegmentName(getDefaultValueHof(defaultAssetName));
        setPopupStep("Wizard");
    };
    useOnEscape(closeModalAndReset);

    const onNewListButtonClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `create new segment from table`);
        setIsCreateNewListModalOpen(true);
    };

    const onCancel = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "click", `cancel segment creation`);
        setIsCreateNewListModalOpen(false);
    };

    const goBack = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "click", `go back to table`);
        closeModalAndReset();
    };

    const analyzeSegment = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "click", `analyse segment`);
        if (!segmentCreated?.current?.id) {
            return;
        }
        const { id } = segmentCreated.current;
        const params = proNavigator.getParams();
        const { country, duration } = params;
        closeModalAndReset();
        proNavigator.go("digitalsuite_segments-analysis-traffic", {
            mode: MODE.single,
            id,
            country,
            duration,
        });
    };

    const showError = (errorKey: string) => {
        dispatch(showErrorToast(translate(errorKey)));
    };
    const onSave = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "click", `save`);
        const { isValidName, assetName } = segmentName;
        if (!isValidName) {
            return;
        }
        setIsCreatingSegment(true);
        const segmentsApiService = new SegmentsApiService();
        const rules = getRules(selectedRows);
        const customSegment = {
            domain,
            rules,
            segmentName: assetName,
        };
        segmentsApiService
            .createCustomSegment(customSegment)
            .then((customSegment) => {
                clearSelection();
                segmentCreated.current = customSegment;
                dispatch(customSegmentCreatedUpdated(customSegment));
                setPopupStep("Checkout");
            })
            .catch((e) => {
                showError("segments.popup.wizard.save.error");
                Sentry.error("error while creating a segment from a table", e);
            })
            .finally(() => setIsCreatingSegment(false));
    };
    const onSegmentNameChange = (assetName: string) => {
        const { isValid: isValidName, errorMsg: errorMessage } = validateSegmentName(assetName);
        const segmentName = { assetName, isValidName, errorMessage };
        setSegmentName(segmentName);
    };
    const getDropdownComponent = () => {
        const { isValidName, assetName } = segmentName;
        const isEmptyName = assetName?.length === 0 || !assetName;
        const saveProps = {
            onSave,
            isDisabled: !isValidName || isEmptyName,
            isLoading: isCreatingSegment,
        };
        return (
            <TableSelectionGroupWebsiteComponent
                appendTo={appendTo}
                onNewListButtonClick={onNewListButtonClick}
                isCreateNewListModalOpen={isCreateNewListModalOpen}
                newGroupNameLabel={translate("table.selection.newgroup.websites.title")}
                groups={[]}
                groupIconName="category"
                ref={dropdownRef}
                mode={ETableSelectionNewGroupDropdownMode.GROUP_LIST}
                newGroupItemText="table.selection.newgroup.websites.create"
                allItemsExistsMessage="table.selection.websites.exists"
                maxItemsMessage="table.selection.websites.max.websites"
                count={selectedRows.length}
                createGroupButtonKey={createGroupButtonKey}
                addToGroupButtonKey={addToGroupButtonKey}
                customModalChildren={
                    <SegmentModal
                        footerProps={{ onCancel, saveProps }}
                        bodyProps={{ onSegmentNameChange, segmentName, isLoading }}
                        step={popupStep}
                        checkoutFooterProps={{ goBack, analyzeSegment }}
                    />
                }
            />
        );
    };
    return (
        <SWReactTableWrapperContextConsumer>
            {({ selectedRows, clearAllSelectedRows }) => {
                const selectionAmount = selectedRows.length;
                const selectedText = `${selectionAmount} ${translate(textKey)}`;
                const onCloseClick = () => {
                    allTrackers.dispatchTrackEvent("Button", "click", `clear selected rows`, {
                        selectionAmount,
                    });
                    clearAllSelectedRows();
                };
                return (
                    <TableSelection
                        showSeparator={false}
                        key="1"
                        selectedText={selectedText}
                        onCloseClick={onCloseClick}
                        isVisible={selectedRows.length > 0 || initialIsVisible}
                        groupSelectorElement={getDropdownComponent()}
                        isDisabled={selectedRows?.length === 0}
                    />
                );
            }}
        </SWReactTableWrapperContextConsumer>
    );
};
